import type { ActivitiesPrepare } from "@b2bportal/activities-api";
import { Product, type ProductOpaqueValue } from "@b2bportal/purchase-api";
import {
  BookingQuestionId,
  BookingQuestionRequired,
  type BookingQuestionAnswer,
} from "@b2bportal/rbc-activities-api";
import { cartQuoteSelectors } from "@checkout/states/Cart/CartQuote";
import { ContactSelectors } from "@checkout/states/common";
import { TravelerSelectors } from "@checkout/states/common/TravelerInformation";
import { ParentState } from "@checkout/types";
import type { State } from "xstate";
import type { PartialParentWithActivityInformation } from "./context";
import type {
  ActivitiesContext,
  ActivityAdditionalDetailsContext,
} from "./types";

export enum PickupPointOption {
  SelectPickup = "SelectPickup",
  PickupLater = "PickupLater",
  NoPickup = "NoPickup",
}

/** Label used when the selected pickup is "custom" / hotel not in list (FREETEXT answer). */
export const HOTEL_OPTION_NOT_AVAILABLE_LABEL = "location-not-found";

type ActivityStateType = State<PartialParentWithActivityInformation>;
type ActivityStateWithoutValue = Pick<ActivityStateType, "context">;

// Extended state type that includes the full activity details context
type ActivityDetailsStateType = State<{
  [ParentState.activityInformation]?: ActivityAdditionalDetailsContext;
}>;
type ActivityDetailsStateWithoutValue = Pick<
  ActivityDetailsStateType,
  "context"
>;

export const getActivityInformationState = ({
  context,
}: ActivityStateType | ActivityStateWithoutValue) => {
  return context[ParentState.activityInformation];
};

export const getActivityInformationVisited = (
  state: ActivityStateWithoutValue
) => getActivityInformationState(state)?.visited;

export const getActivityInformationValidated = (
  state: ActivityDetailsStateType | ActivityDetailsStateWithoutValue
): boolean => {
  const activityInfo = state.context[ParentState.activityInformation];
  const {
    bookingQuestions,
    selectedPickupPoint,
    specialRequirement,
    pickupLocationAnswer,
    isPickupIncluded,
    selectedTransferArrivalMode,
    selectedTransferDepartureMode,
    transferArrivalOtherText,
    transferDepartureOtherText,
  } = activityInfo;

  // Pick up validation - only validate if pickup is included for the selected bookable item
  const isPickupPointMandatory =
    (isPickupIncluded ?? false) &&
    bookingQuestions?.find((q) => q.id === BookingQuestionId.PickupPoint)
      ?.required === BookingQuestionRequired.Mandatory;
  const pickUpPointIsValid =
    !isPickupPointMandatory ||
    (selectedPickupPoint === PickupPointOption.SelectPickup
      ? pickupLocationAnswer !== undefined
      : selectedPickupPoint !== undefined);

  // Special requirements validation
  const isSpecialRequirementsMandatory =
    bookingQuestions?.find(
      (q) => q.id === BookingQuestionId.SpecialRequirements
    )?.required === BookingQuestionRequired.Mandatory;
  const specialRequirementIsValid =
    !isSpecialRequirementsMandatory ||
    (specialRequirement !== undefined && specialRequirement.trim() !== "");

  // Transfer arrival mode validation
  const isTransferArrivalModeMandatory =
    bookingQuestions?.find(
      (q) => q.id === BookingQuestionId.TransferArrivalMode
    )?.required === BookingQuestionRequired.Mandatory;
  const transferArrivalModeIsValid =
    !isTransferArrivalModeMandatory ||
    (selectedTransferArrivalMode !== undefined &&
      (selectedTransferArrivalMode !== "OTHER" ||
        (transferArrivalOtherText !== undefined &&
          transferArrivalOtherText.trim() !== "")));

  // Transfer departure mode validation
  const isTransferDepartureModeMandatory =
    bookingQuestions?.find(
      (q) => q.id === BookingQuestionId.TransferDepartureMode
    )?.required === BookingQuestionRequired.Mandatory;
  const transferDepartureModeIsValid =
    !isTransferDepartureModeMandatory ||
    (selectedTransferDepartureMode !== undefined &&
      (selectedTransferDepartureMode !== "OTHER" ||
        (transferDepartureOtherText !== undefined &&
          transferDepartureOtherText.trim() !== "")));

  // Validate conditional transfer questions for both arrival and departure
  const transferArrivalModeQuestion = bookingQuestions?.find(
    (q) => q.id === BookingQuestionId.TransferArrivalMode
  );
  const transferDepartureModeQuestion = bookingQuestions?.find(
    (q) => q.id === BookingQuestionId.TransferDepartureMode
  );

  const arrivalConditionalQuestionIds =
    selectedTransferArrivalMode && transferArrivalModeQuestion
      ? transferArrivalModeQuestion.conditionallyRequiredQuestions?.[
          selectedTransferArrivalMode
        ] || []
      : [];

  const departureConditionalQuestionIds =
    selectedTransferDepartureMode && transferDepartureModeQuestion
      ? transferDepartureModeQuestion.conditionallyRequiredQuestions?.[
          selectedTransferDepartureMode
        ] || []
      : [];

  const allConditionalQuestionIds = [
    ...arrivalConditionalQuestionIds,
    ...departureConditionalQuestionIds,
  ];

  const conditionalAnswersValid = allConditionalQuestionIds.every(
    (questionId) => {
      const question = bookingQuestions?.find((q) => q.id === questionId);
      if (
        !question ||
        question.required !== BookingQuestionRequired.Mandatory
      ) {
        return true; // Not mandatory, so valid
      }

      const answer = activityInfo.transferConditionalAnswers?.[questionId];
      return answer && answer.trim() !== "";
    }
  );

  return (
    pickUpPointIsValid &&
    specialRequirementIsValid &&
    transferArrivalModeIsValid &&
    transferDepartureModeIsValid &&
    conditionalAnswersValid
  );
};

// Types for the full activities context needed for prepare fulfillment
type ActivitiesFullStateType = State<ActivitiesContext>;
type ActivitiesFullStateWithoutValue = Pick<ActivitiesFullStateType, "context">;

export const getBookingQuestionAnswers = (
  state: ActivityDetailsStateType | ActivityDetailsStateWithoutValue
): BookingQuestionAnswer[] => {
  const activityInfo = state.context[ParentState.activityInformation];
  if (!activityInfo) return [];

  const answers: BookingQuestionAnswer[] = [];

  const {
    selectedPickupPoint,
    hotelSelection,
    hotelName = "",
    address = "",
    city = "",
    zipCode = "",
    isPickupIncluded,
    bookingQuestions,
  } = activityInfo;

  const isCustomPickupLocation =
    hotelSelection?.activityLocation?.name ===
      HOTEL_OPTION_NOT_AVAILABLE_LABEL ||
    activityInfo.pickupLocationAnswer?.answer ===
      HOTEL_OPTION_NOT_AVAILABLE_LABEL;

  // Check if pickup question is mandatory
  const isPickupQuestionMandatory =
    bookingQuestions?.find((q) => q.id === BookingQuestionId.PickupPoint)
      ?.required === BookingQuestionRequired.Mandatory;

  // Handle pickup answers based on whether pickup is included for the selected bookable item
  if (isPickupIncluded ?? false) {
    // Pickup is included - handle all pickup options
    if (
      selectedPickupPoint === PickupPointOption.SelectPickup &&
      (hotelSelection || activityInfo.pickupLocationAnswer)
    ) {
      if (isCustomPickupLocation) {
        answers.push({
          bookingQuestionId: BookingQuestionId.PickupPoint,
          answer: [hotelName, address, city, zipCode].join(", "),
          unit: "FREETEXT",
        });
      } else if (hotelSelection) {
        answers.push({
          bookingQuestionId: BookingQuestionId.PickupPoint,
          answer: hotelSelection.locationRef,
          unit: "LOCATION_REFERENCE",
        });
      } else if (activityInfo.pickupLocationAnswer) {
        answers.push({
          bookingQuestionId: BookingQuestionId.PickupPoint,
          answer: activityInfo.pickupLocationAnswer.answer,
          unit: activityInfo.pickupLocationAnswer.unit ?? "LOCATION_REFERENCE",
        });
      }
    } else if (selectedPickupPoint === PickupPointOption.PickupLater) {
      answers.push({
        bookingQuestionId: BookingQuestionId.PickupPoint,
        answer: "CONTACT_SUPPLIER_LATER",
        unit: "LOCATION_REFERENCE",
      });
    } else if (selectedPickupPoint === PickupPointOption.NoPickup) {
      answers.push({
        bookingQuestionId: BookingQuestionId.PickupPoint,
        answer: "MEET_AT_DEPARTURE_POINT",
        unit: "LOCATION_REFERENCE",
      });
    }
  } else if (isPickupQuestionMandatory) {
    // Pickup is not included BUT the activity has a mandatory pickup question
    // Answer with "no pickup required"
    answers.push({
      bookingQuestionId: BookingQuestionId.PickupPoint,
      answer: "MEET_AT_DEPARTURE_POINT",
      unit: "LOCATION_REFERENCE",
    });
  }

  if (activityInfo.specialRequirement?.trim()) {
    answers.push({
      bookingQuestionId: BookingQuestionId.SpecialRequirements,
      answer: activityInfo.specialRequirement,
    });
  }

  // Handle TransferArrivalMode if selected
  if (activityInfo.selectedTransferArrivalMode) {
    const arrivalAnswer =
      activityInfo.selectedTransferArrivalMode === "OTHER"
        ? (activityInfo.transferArrivalOtherText ?? "")
        : activityInfo.selectedTransferArrivalMode;
    answers.push({
      bookingQuestionId: BookingQuestionId.TransferArrivalMode,
      answer: arrivalAnswer,
      unit:
        activityInfo.selectedTransferArrivalMode === "OTHER"
          ? "FREETEXT"
          : activityInfo.selectedTransferArrivalMode,
    });
  }

  // Handle TransferDepartureMode if selected
  if (activityInfo.selectedTransferDepartureMode) {
    const departureAnswer =
      activityInfo.selectedTransferDepartureMode === "OTHER"
        ? (activityInfo.transferDepartureOtherText ?? "")
        : activityInfo.selectedTransferDepartureMode;
    answers.push({
      bookingQuestionId: BookingQuestionId.TransferDepartureMode,
      answer: departureAnswer,
      unit:
        activityInfo.selectedTransferDepartureMode === "OTHER"
          ? "FREETEXT"
          : activityInfo.selectedTransferDepartureMode,
    });
  }

  // Handle conditional transfer questions
  if (
    activityInfo.selectedTransferArrivalMode ||
    activityInfo.selectedTransferDepartureMode
  ) {
    const conditionalAnswers = activityInfo.transferConditionalAnswers || {};
    Object.entries(conditionalAnswers).forEach(([questionId, answer]) => {
      if (typeof answer === "string" && answer.trim()) {
        answers.push({
          bookingQuestionId: questionId as BookingQuestionId,
          answer,
          unit: "FREETEXT",
        });
      }
    });
  }

  // Get selected traveler IDs to recalculate travelerNum based on current order
  const selectedTravelerIds =
    TravelerSelectors.getSelectedTravelerIds(state as any) ?? [];

  // Include per-traveler booking question answers (first name, last name, DOB, passport, height, weight, age band, etc.)
  // Filter to only include answers for currently selected travelers, and recalculate travelerNum based on current order
  const travelerAnswers = (activityInfo.travelerBookingQuestionAnswers ?? [])
    .filter((a) => selectedTravelerIds.includes(a.travelerId ?? ""))
    .map((a) => {
      const idx = selectedTravelerIds.indexOf(a.travelerId ?? "");
      const travelerNum = idx >= 0 ? idx + 1 : a.travelerNum;
      return {
        bookingQuestionId: a.bookingQuestionId,
        answer: a.answer,
        ...(a.unit !== undefined && { unit: a.unit }),
        travelerNum,
      };
    });
  answers.push(...travelerAnswers);

  return answers;
};

export const getSelectedLanguageGuide = (
  state: ActivityDetailsStateType | ActivityDetailsStateWithoutValue
) => state.context[ParentState.activityInformation]?.selectedLangGuide;

export const getActivitiesShopContext = (
  state: ActivitiesFullStateType | ActivitiesFullStateWithoutValue
) => state.context[ParentState.activitiesShop];

// Get the activity confirmation ID from fulfilled products
export const getActivityConfirmationId = (
  state: ActivitiesFullStateType | ActivitiesFullStateWithoutValue
) => {
  const products = state.context[ParentState.cartFulfill]?.fulfilledProducts;
  const activityProduct = products?.find((p) => p.type === Product.Activities);
  return activityProduct?.value?.customerReservationId;
};

export const getActivitiesPrepareFulfillmentDetails = (
  state: ActivitiesFullStateType | ActivitiesFullStateWithoutValue
): ProductOpaqueValue | undefined => {
  const activityInfo = state.context[ParentState.activityInformation];
  const activitiesShop = state.context[ParentState.activitiesShop];
  const prepareFulfillmentData = activitiesShop?.prepareFulfillmentData;

  const quotedBalance = cartQuoteSelectors.getCartQuotedBalance(state as any);

  const contactInfo = ContactSelectors.getContactInformation(state as any);
  const formattedPhone = ContactSelectors.getFormattedPhoneNumber(state as any);

  const travelers = TravelerSelectors.getTravelers(state as any);
  const selectedTravelerIds = TravelerSelectors.getSelectedTravelerIds(
    state as any
  );
  const primaryTravelerId =
    activityInfo?.primaryParticipantId ?? selectedTravelerIds?.[0];
  const primaryTraveler = primaryTravelerId
    ? travelers?.find((t) => t.id === primaryTravelerId)
    : undefined;

  // If we don't have essential data, return undefined
  if (!contactInfo?.email || !prepareFulfillmentData?.activityId) {
    return undefined;
  }

  // Build booking question answers
  const bookingQuestionAnswers = getBookingQuestionAnswers(state);

  const travelersList =
    travelers
      ?.filter((t) => selectedTravelerIds?.includes(t.id))
      ?.map((t) => ({
        firstName: t.name.first ?? "",
        lastName: t.name.last ?? "",
      })) ?? [];

  const bookerInfo = {
    firstName: primaryTraveler?.name.first ?? "",
    lastName: primaryTraveler?.name.last ?? "",
  };

  const totalPurchasePrice =
    quotedBalance?.fiat ?? prepareFulfillmentData.totalPurchasePrice;

  const activitiesPrepare: ActivitiesPrepare = {
    communication: {
      email: contactInfo.email,
      phone: formattedPhone || contactInfo.phoneNumber || "",
    },
    bookerInfo,
    activityDetails: {
      totalPurchasePrice,
      startTime: prepareFulfillmentData.startTime,
      activityId: prepareFulfillmentData.activityId,
      productTitle: prepareFulfillmentData.productTitle,
      numberOfTravelers: prepareFulfillmentData.numberOfTravelers,
      activityOptionId: prepareFulfillmentData.activityOptionId,
      startLocation: prepareFulfillmentData.startLocation,
      durationMinutes: prepareFulfillmentData.durationMinutes,
      category: prepareFulfillmentData.category,
      travelers: travelersList,
      startDate: prepareFulfillmentData.startDate,
    },
    bookingQuestionAnswers,
    languageGuide: activityInfo?.selectedLangGuide,
  };

  return {
    type: Product.Activities,
    value: activitiesPrepare,
  };
};
